import type { ReactElement, ReactNode } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  LayoutGrid,
  List,
  Plus,
} from 'lucide-react';
import { useShowMealsPref, setShowMealsPref } from './showMealsPref';

export type CalendarView = 'day' | 'week' | 'month' | 'list';

/**
 * Toolbar del calendario `/entrenos` (M2.19).
 *
 * Contiene:
 *  - Toggle de vistas (Día / Semana / Mes / Lista) — segmented control en
 *    desktop, `<select>` en mobile (<md).
 *  - Navegación de rango (← / Hoy / →). El paso lo decide el padre según
 *    la vista activa (día/sem/mes/año si lista). Para la vista Lista los
 *    botones quedan deshabilitados.
 *  - Botón "+ Añadir" (deshabilitado por ahora con tooltip
 *    "Próximamente").
 *
 * El rangeLabel ya viene formateado por el padre (p. ej. "Mayo 2026",
 * "12-18 May 2026", "Martes 12 de mayo, 2026").
 */
export function CalendarToolbar({
  view,
  onViewChange,
  rangeLabel,
  onPrev,
  onNext,
  onToday,
  navDisabled = false,
  extraActions,
}: {
  view: CalendarView;
  onViewChange: (v: CalendarView) => void;
  rangeLabel: string;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  navDisabled?: boolean;
  /**
   * M2.32.C — Slot opcional para acciones contextuales (ej. botón
   * "Reajustar semana") que sólo tienen sentido en vistas Día/Semana.
   * Se renderiza a la izquierda del botón "Añadir" en desktop, debajo
   * en mobile (estructura flex existente).
   */
  extraActions?: ReactNode;
}): ReactElement {
  return (
    <div className="flex flex-col gap-3 border-b border-slate-200 pb-3 sm:flex-row sm:items-center sm:justify-between">
      {/* Izquierda: navegación de rango */}
      <div className="flex items-center gap-2">
        <div
          role="toolbar"
          aria-label="Navegación del calendario"
          className="flex items-center gap-1"
        >
          <button
            type="button"
            onClick={onPrev}
            disabled={navDisabled}
            aria-label="Anterior"
            data-testid="calendar-nav-prev"
            className="inline-flex h-8 w-8 items-center justify-center rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={onToday}
            disabled={navDisabled}
            data-testid="calendar-nav-today"
            className="rounded border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Hoy
          </button>
          <button
            type="button"
            onClick={onNext}
            disabled={navDisabled}
            aria-label="Siguiente"
            data-testid="calendar-nav-next"
            className="inline-flex h-8 w-8 items-center justify-center rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        <p
          role="status"
          aria-live="polite"
          data-testid="calendar-range-label"
          className="text-sm font-medium text-slate-900"
        >
          {rangeLabel}
        </p>
      </div>

      {/* Derecha: toggle vistas + Añadir */}
      <div className="flex items-center gap-2">
        {/* Mobile: select */}
        <label className="sm:hidden">
          <span className="sr-only">Vista del calendario</span>
          <select
            value={view}
            onChange={(e) => onViewChange(e.target.value as CalendarView)}
            data-testid="calendar-view-select"
            className="rounded border border-slate-300 bg-white px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1"
          >
            <option value="day">Día</option>
            <option value="week">Semana</option>
            <option value="month">Mes</option>
            <option value="list">Lista</option>
          </select>
        </label>

        {/* Desktop: segmented control */}
        <div
          role="tablist"
          aria-label="Vista del calendario"
          className="hidden items-center gap-0.5 rounded border border-slate-300 bg-white p-0.5 sm:inline-flex"
        >
          <ViewTab
            label="Día"
            value="day"
            current={view}
            onChange={onViewChange}
            icon={<CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />}
          />
          <ViewTab
            label="Semana"
            value="week"
            current={view}
            onChange={onViewChange}
            icon={<LayoutGrid className="h-3.5 w-3.5" aria-hidden="true" />}
          />
          <ViewTab
            label="Mes"
            value="month"
            current={view}
            onChange={onViewChange}
            icon={<LayoutGrid className="h-3.5 w-3.5" aria-hidden="true" />}
          />
          <ViewTab
            label="Lista"
            value="list"
            current={view}
            onChange={onViewChange}
            icon={<List className="h-3.5 w-3.5" aria-hidden="true" />}
          />
        </div>

        {extraActions}

        {/* M2.34.C — Toggle "Mostrar comidas" (persistente, default ON). */}
        <ShowMealsToggle />

        <button
          type="button"
          disabled
          title="Próximamente"
          aria-label="Añadir entreno (próximamente)"
          data-testid="calendar-add"
          className="inline-flex items-center gap-1 rounded border border-teal-600 bg-teal-600/10 px-2.5 py-1 text-xs font-medium text-teal-700 hover:bg-teal-600/15 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <Plus className="h-3.5 w-3.5" aria-hidden="true" />
          Añadir
        </button>
      </div>
    </div>
  );
}

/**
 * M2.34.C — Switch compacto "🍽 Mostrar comidas". Estado en localStorage
 * via `useShowMealsPref`. Default ON. Reutilizado por `DayView` (futuro: y
 * por `WeekView`/`MonthView` cuando los abramos al toggle).
 */
function ShowMealsToggle(): ReactElement {
  const show = useShowMealsPref();
  return (
    <button
      type="button"
      role="switch"
      aria-checked={show}
      aria-label={
        show
          ? 'Ocultar comidas del calendario'
          : 'Mostrar comidas del calendario'
      }
      data-testid="calendar-show-meals-toggle"
      data-state={show ? 'on' : 'off'}
      onClick={() => setShowMealsPref(!show)}
      title={show ? 'Ocultar comidas' : 'Mostrar comidas'}
      className={`inline-flex items-center gap-1 rounded border px-2.5 py-1 text-xs font-medium transition focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1 ${
        show
          ? 'border-amber-400 bg-amber-100 text-amber-900 hover:bg-amber-200'
          : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-100'
      }`}
    >
      <span aria-hidden="true">🍽</span>
      <span className="hidden sm:inline">
        {show ? 'Comidas' : 'Sin comidas'}
      </span>
    </button>
  );
}

function ViewTab({
  label,
  value,
  current,
  onChange,
  icon,
}: {
  label: string;
  value: CalendarView;
  current: CalendarView;
  onChange: (v: CalendarView) => void;
  icon: ReactElement;
}): ReactElement {
  const active = current === value;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      aria-label={`Vista ${label}`}
      data-testid={`calendar-view-${value}`}
      onClick={() => onChange(value)}
      className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1 ${
        active
          ? 'bg-teal-600 text-white'
          : 'text-slate-700 hover:bg-slate-100'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}
